import * as React from "react";
import { styled } from "@mui/system";
import MUITabs from "@mui/material/Tabs";
import { TabsProps as TabsPropsMUI } from "@mui/material/Tabs";

export interface ExtendedTabsProps extends TabsPropsMUI {
  /**
   * If want to change size of the container and font-size.
   * @default medium
   */
  size?: "small" | "medium";
  /**
   *
   * @default false
   */
  secondary?: boolean;
  /**
   *
   * @default false
   */
  sticky?: boolean;
}

export const ExtendedTabs = styled(
  React.forwardRef((props: ExtendedTabsProps, ref: any) => (
    <MUITabs {...props} ref={ref} />
  )),
  {
    name: "ExtendedTabs",
    slot: "Root",
    shouldForwardProp: (prop) =>
      prop !== "secondary" && prop !== "sticky" && prop !== "myProp",
    overridesResolver: (props, styles) => []
  }
)(({ theme, variant, size, secondary, sticky, orientation }) => ({
  "&.MuiTabs-root": {
    alignItems: "center",
    borderBottom: "1px solid " + theme.palette.primary.borderDefault,
    transition: "none"
  },
  "&.MuiTabs-root .MuiTabs-scroller .MuiTabs-indicator": {
    height: 0
  },
  "&.MuiTabs-root .MuiTabs-scrollButtons.Mui-disabled": {
    opacity: 0.3
  },
  "&.MuiTabs-root .MuiTabs-scrollButtons > .MuiSvgIcon-root": {
    fontSize: 20
  },

  "&.MuiTabs-root .MuiTabs-scroller .MuiTabs-flexContainer ": {
    height:
      {
        standard: size === "medium" ? 72 : size === "small" && 48,
        scrollable: size === "medium" ? 72 : size === "small" && 48
      }[variant] || 72
  },
  "&.MuiTabs-root .MuiTabs-scroller .MuiTabs-flexContainer .MuiTab-root": {
    fontSize: {
      medium: 24,
      small: 16
    }[size]
  },

  //secondary
  ...(secondary && {
    "&.MuiTabs-root .MuiTabs-scroller .MuiTabs-indicator": {
      height: 0
    },
    "&.MuiTabs-root .MuiTabs-scroller .MuiTabs-flexContainer": {
      height: 48,
      alignItems: "center"
    },
    "&.MuiTabs-root .MuiTabs-scroller .MuiTabs-flexContainer .MuiTab-root": {
      padding: "4px 12px",
      margin: "0 12px",
      fontSize: 14,
      fontFamily: "Cairo",
      fontWeight: 600,
      color: theme.palette.primary.textSubduedColor,
      ":hover": {
        minHeight: 32,
        height: 32,
        background: theme.palette.primary.interactiveDefaultColor,
        color: theme.palette.primary.baseWhiteColor,
        padding: "4px 12px",
        borderBottom: "0 !important",
        borderRadius: 2
      }
    },
    "&.MuiTabs-root .MuiTabs-scroller .MuiTabs-flexContainer .MuiTab-root.Mui-selected":
      {
        minHeight: 32,
        height: 32,
        background: "#B9E6F6",
        color: theme.palette.primary.textTitleColor,
        padding: "4px 12px",
        borderBottom:
          "1px solid " +
          theme.palette.primary.interactiveDefault +
          " !important",
        border: "1px solid " + theme.palette.primary.interactiveDefault,
        borderRadius: 2
      },
    "&.MuiTabs-root .MuiTabs-scroller .MuiTabs-flexContainer .MuiTab-root.Mui-disabled":
      {
        margin: "0 12px",
        color: "#D8D8D9",
        border: "none",
        background: "transparent"
      },
    "&.MuiTabs-root .MuiTabs-scroller .MuiTabs-flexContainer .MuiTab-root.Mui-disabled:hover":
      {
        background: "transparent",
        borderBottom: "0 !important"
      }
  }),

  //vertical tabs
  "&.MuiTabs-root.MuiTabs-vertical": {
    borderBottom: 0,
    position: "fixed",
    right: "40%",
    top: "31%",
    boxShadow: theme.shadows[1]
  },
  "&.MuiTabs-root.MuiTabs-vertical .MuiTabs-fixed .MuiTabs-indicator": {
    display: "none"
  },
  "&.MuiTabs-root .MuiTabs-fixed .MuiTabs-flexContainerVertical": {
    height: "100%",
    maxWidth: 40,
    width: 40,
    paddingTop: 4,
    paddingBottom: 4,
    paddingLeft: 4,
    paddingRight: 4
  },
  "&.MuiTabs-root .MuiTabs-fixed .MuiTabs-flexContainerVertical .MuiTab-root": {
    width: 40,
    minWidth: 40,
    minHeight: 40,
    marginBottom: 8,
    ":last-child": {
      marginBottom: 0
    },
    padding: 0,
    "& svg": {
      color: theme.palette.primary.borderActive
    }
  },
  "&.MuiTabs-root .MuiTabs-fixed .MuiTabs-flexContainerVertical .MuiButtonBase-root.MuiTab-root.Mui-selected":
    {
      background: theme.palette.primary.background + " !important",
      borderRadius: 2,
      borderBottom: "none !important"
    },
  ...(orientation === "vertical" && {
    "&.MuiTabs-root.MuiTabs-vertical": {
      borderBottom: "none",
      "& .MuiTab-root:hover": {
        background: theme.palette.primary.interactiveSelectedColor
      }
    }
  }),
  ...(sticky &&
    orientation === "vertical" && {
      "&.MuiTabs-root.MuiTabs-vertical": {
        position: "absolute",
        right: "0 !important",
        "& .MuiTab-root:hover": {
          background: theme.palette.primary.interactiveSelectedColor
        }
      }
    })
}));
